import React, { useEffect, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useColorMode } from '@chakra-ui/react';
import { AppDispatch, RootState } from '../redux/store';
import { toggleTheme, setTheme, initializeTheme } from '../redux/Theme';

type ThemeMode = 'light' | 'dark';

// Simple provider that initializes theme on mount and syncs with Chakra UI
export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { theme } = useSelector((state: RootState) => state.theme);
  const { colorMode, setColorMode } = useColorMode();

  useEffect(() => {
    // Initialize theme on mount
    dispatch(initializeTheme());
  }, [dispatch]);

  // Sync Chakra UI color mode with Redux theme
  useEffect(() => {
    if (theme && colorMode !== theme) {
      setColorMode(theme);
    }
  }, [theme, colorMode, setColorMode]);

  return <>{children}</>;
};

// Hook to use theme from Redux store
export const useTheme = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { theme, isDarkMode } = useSelector((state: RootState) => state.theme);

  const handleToggleTheme = () => {
    dispatch(toggleTheme());
  };

  const handleSetTheme = (newTheme: ThemeMode) => {
    dispatch(setTheme(newTheme));
  };

  return {
    theme,
    isDarkMode,
    toggleTheme: handleToggleTheme,
    setTheme: handleSetTheme,
  };
};

